<template>
  <div v-if="showNewVersion">
    <div
      v-if="fetching"
      class="loading-bar-container"
    >
      <loading-bar />
    </div>
    <div
      v-else
      class="possibility-of-passing-widget-component pop-v2"
      :class="{expanded}"
    >
      <div class="pop-preview">
        <div class="widget-label">
          Possibility of Passing
        </div>
        <p
          v-if="!expanded"
          class="widget-info"
        >
          {{ widgetInfo }}
        </p>
        <button
          class="expand-button"
          @click="setExpanded(!expanded)"
        >
          <icon
            class="expand-button-icon"
            :name="expanded ? 'cross-thin' : 'arrow-down'"
            variant="mastery"
          />
        </button>
      </div>
      <div
        v-if="expanded"
        class="pop-details"
      >
        <div class="pop-arc-card">
          <results-arc
            v-if="hasResults"
            :percent="possibility"
            :results-date="lastAdjustmentDate"
          />
          <progress-arc
            v-else
            :ready="false"
            :locked="locked"
            :percent="progressPercent"
            :questions-remaining="nextAdjustment"
            @fetch-results="handleReadyClick"
          />
        </div>
        <div class="pop-aspect-card-container">
          <div
            v-for="flipcard in flipcards"
            :key="flipcard.id"
            class="pop-aspect-card"
          >
            <span
              v-if="!hasResults"
              class="empty-value"
            >--</span>
            <span
              v-else
              class="value"
            >{{ flipcardValue(flipcard) }}</span>
            <span class="title">{{ flipcardTitle(flipcard) }}</span>
            <span class="description">
              {{ flipcardDescription(flipcard) }}
            </span>
          </div>
        </div>
      </div>
    </div>
    <beta-modal />
  </div>
  <div
    v-else
    class="possibility-of-passing-widget-component"
  >
    <dashboard-widget-header
      :title="(config && config.name) || 'Possibility of Passing'"
      :post-header-icon="{ name: 'info-outline', variant: 'mastery' }"
      subtitle="beta"
      @post-header-icon-click="$modal.show('pop-beta-modal')"
    />
    <div
      v-if="fetching"
      class="loading-bar-container"
    >
      <loading-bar />
    </div>
    <div
      v-else
      class="cards-container"
    >
      <div class="card focused-card">
        <results-arc
          v-if="showResults"
          :percent="possibility"
          :results-date="lastAdjustmentDate"
        />
        <progress-arc
          v-else
          :ready="resultsReady"
          :locked="locked"
          :percent="progressPercent"
          :questions-remaining="nextAdjustment"
          @fetch-results="handleReadyClick"
        />
      </div>
      <div class="metadata">
        <flipcard
          v-for="flipcard in flipcards"
          :key="flipcard.id"
          :flipcard="flipcard"
          :locked="locked"
          :show-results="showResults"
        />
      </div>
    </div>
    <beta-modal />
  </div>
</template>

<script>
import BetaModal from '@/components/ui/pop/beta-modal';
import DashboardWidgetHeader from '@/components/dashboard-widgets/header';
import Flipcard from '@/components/ui/pop/flipcard';
import Icon from '@/components/singletons/icon';
import LoadingBar from '@/components/singletons/loading-bar';
import ProgressArc from '@/components/ui/pop/progress-arc';
import ResultsArc from '@/components/ui/pop/results-arc';

import DashboardWidgetMixin from '@/mixins/dashboardWidget';

import { setChecklistData, REVIEW_POP_KEY } from '@/utils/checklist';
import { PAID_FEATURES } from '@/utils/acl';

const flipcards = [
  {
    id: 1,
    title: () => 'Next Odds Update',
    description: (val) => `We'll re-calculate your odds as soon as you've answered another ${val} questions.`,
    value: 'nextAdjustment',
  },
  {
    id: 2,
    title: (val) => `Question${val > 1 ? 's' : ''} Completed`,
    description: (val) => `A few questions don't fit our formula for finding your odds, but these ${val} did.`,
    value: 'questionsConsidered',
  },
  {
    id: 3,
    title: () => 'How Sure Are We?',
    description: () => 'The accuracy of your odds improves as you answer questions. Sample size, ftw!',
    value: 'confidence',
    valueType: 'percentage',
  },
  {
    id: 4,
    title: (val) => `Adjustment${val > 1 ? 's' : ''} to Odds`,
    description: (val) => `We've pulled new data to update your possibility of passing ${val} times so far.`,
    value: 'timesAdjusted',
  },
];

export default {
  components: {BetaModal, DashboardWidgetHeader, Flipcard, Icon, LoadingBar, ProgressArc, ResultsArc},
  mixins: [DashboardWidgetMixin],
  props: {
    config: {
      type: Object,
    },
  },
  data() {
    return {
      expanded: false,
      fetching: true,
      hasResults: false,
      resultsRevealed: false,
      showNewVersion: false,

      // API values
      confidence: null,
      lastAdjustmentDate: null,
      nextAdjustment: null,
      possibility: null,
      questionsAnswered: null,
      questionsConsidered: null,
      timesAdjusted: null,
    };
  },
  computed: {
    flipcards() {
      return flipcards.map(card => ({...card, value: this[card.value]}));
    },
    locked() {
      if (this.showNewVersion) {
        return false;
      }

      return !this.$access(this.$store, 'feature', PAID_FEATURES.POSSIBILITY_OF_PASSING);
    },
    percentDecimal() {
      return (this.possibility || 1) / 100;
    },
    percentRange() {
      if (this.percentDecimal < .6) {
        return '0-59%';
      } else if (this.percentDecimal >= .6 && this.percentDecimal < .8) {
        return '60-79%';
      } else if (this.percentDecimal >= .8) {
        return '80-99%';
      }
    },
    progressPercent() {
      const total = this.questionsAnswered + this.nextAdjustment;
      return (this.questionsAnswered / total) * 100;
    },
    resultsReady() {
      return this.possibility !== null && this.possibility !== undefined;
    },
    showResults() {
      return this.hasResults && this.resultsRevealed && !this.locked;
    },
    storageKey() {
      const user = this.$store.getters['session/currentUser'];
      return `mnm-pop-results-revealed-${user.id}`;
    },
    widgetInfo() {
      if (this.hasResults) {
        return this.percentRange;
      } else {
        return `${this.questionsAnswered} / ${this.questionsAnswered + this.nextAdjustment}`;
      }
    },
  },
  created() {
    this.checkApptimize();
    this.fetchPoP();
    this.getReadyFlag();
  },
  methods: {
    checkApptimize() {
      const user = this.$store.getters['session/currentUser'];
      if (user && user.previewer) {
        this.showNewVersion = true;
      } else {
        const apptimizeEnabled = this.$apptimize.checkExperiments({
          experimentName: 'Possibility of Passing Widget',
          bool: 'PoPV2Enabled',
          defaultVal: false,
          matcher: /Possibility of Passing/,
        });
        this.showNewVersion = apptimizeEnabled;
      }
    },
    completeChecklistItem() {
      setChecklistData(this.$store, REVIEW_POP_KEY, this.$notify);
    },
    fetchPoP() {
      this.$store.getters['session/ajax'].get('/possibility_of_passing').then(({ data }) => {
        this.setPoP(data);
        this.fetching = false;
      }).catch(error => console.log(error));
    },
    flipcardValue(card) {
      if (card.valueType === 'percentage') {
        return `${card.value}%`;
      } else {
        return card.value;
      }
    },
    flipcardDescription(card) {
      return card.description(card.value, card.secondaryValue);
    },
    flipcardTitle(card) {
      return card.title(card.value);
    },
    getReadyFlag() {
      const ls = localStorage.getItem(this.storageKey);
      this.resultsRevealed = ls === 'true';
    },
    handleReadyClick() {
      if (this.locked) {
        this.$store.dispatch('ui/toggleUpsellModal', { show: true, feature: 'Possibility of Passing' });
      } else {
        localStorage.setItem(this.storageKey, 'true');
        this.resultsRevealed = true;
        this.completeChecklistItem();
        if (this.timesAdjusted === 1) {
          this.$analytics.events.calculatedInitialScore(this.questionsAnswered, this.possibility);
        }
      }
    },
    setPoP(apiData) {
      this.confidence = apiData.confidence;
      this.lastAdjustmentDate = new Date(apiData.calculatedAt * 1000);
      this.nextAdjustment = apiData.nextAdjustment;
      this.possibility = apiData.possibilityOfPassing;
      this.questionsAnswered = apiData.questionsAnswered;
      this.questionsConsidered = apiData.questionsConsidered;
      this.timesAdjusted = apiData.totalAdjustments;
      // possibilityOfPassing can be 0 so we need to explicitly check for undefined and null
      if (apiData.possibilityOfPassing !== undefined && apiData.possibilityOfPassing !== null) {
        this.hasResults = true;
      }
    },
    setExpanded(expand) {
      if (expand) {
        this.completeChecklistItem();
        this.$analytics.events.viewedScoreFactor();
      }
      this.expanded = expand;
    },
  },
};
</script>

<style lang='scss' scoped>
.possibility-of-passing-widget-component:not(.pop-v2) {
  @include padded-widget;

  .loading-bar-container {
    padding: rem-calc(124) 0; // half of the final height to center the loading bar
  }

  .cards-container {
    max-width: rem-calc(760);
    height: rem-calc(252);
    padding: rem-calc(6);
    -webkit-overflow-scrolling: touch;
    -ms-overflow-style: none;
    scrollbar-width: none;
    overflow-x: auto;
    overflow-y: hidden;
    white-space: nowrap;

    &::-webkit-scrollbar {
      display: none;
    }

    .card {
      border-radius: 4px;
      box-shadow: 0 2px 6px 0 rgba(0,0,0,0.1);
      display: inline-block;
    }

    .focused-card {
      width: rem-calc(300);
      position: relative;
      vertical-align: top;
      height: 100%;
      white-space: initial;
    }

    .metadata {
      vertical-align: top;
      display:inline-block;
      width: rem-calc(420);
      height: 100%;
      margin-left: rem-calc(16);
      white-space: initial;
    }
  }
}

.possibility-of-passing-widget-component.pop-v2 {
  align-items: center;
  border-radius: 8px;
  box-shadow: 0 2px 12px 0 rgba(34, 34, 34, 0.16);
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  height: rem-calc(56);
  margin: rem-calc(32) 0;
  padding: rem-calc(16);
  transition: .25s ease;

  &.expanded {
    height: rem-calc(320);
  }

  .pop-preview {
    align-items: center;
    display: flex;
    height: rem-calc(24);
    width: 100%;

    .widget-label {
      flex: 1;
      font-size: rem-calc(13);
      font-weight: $bold;
    }

    .widget-info {
      @include charter-black;
      font-size: rem-calc(13);
      margin-right: rem-calc(16);
    }

    .expand-button {
      background-color: transparent;
      padding: 0;

      .expand-button-icon {
        height: rem-calc(12);
        width: rem-calc(12);
      }
    }
  }

  .pop-details {
    max-width: 100%;
    height: rem-calc(252);
    padding: rem-calc(6);
    -webkit-overflow-scrolling: touch;
    -ms-overflow-style: none;
    scrollbar-width: none;
    overflow-x: auto;
    overflow-y: hidden;
    white-space: nowrap;

    &::-webkit-scrollbar {
      display: none;
    }

    .pop-arc-card {
      display: inline-block;
      height: 100%;
      position: relative;
      vertical-align: top;
      white-space: initial;
      width: rem-calc(300);
    }

    .pop-aspect-card-container {
      vertical-align: top;
      display:inline-block;
      width: rem-calc(420);
      height: 100%;
      margin-left: rem-calc(16);
      white-space: initial;

      .pop-aspect-card {
        border-radius: 4px;
        display: inline-block;
        font-size: rem-calc(12);
        height: rem-calc(117);
        margin-bottom: rem-calc(15);
        position: relative;
        width: rem-calc(202);

        &:nth-child(1),
        &:nth-child(3) {
          margin-right: rem-calc(16);
        }

        &:nth-child(3),
        &:nth-child(4) {
          margin-bottom: 0;
        }

        .empty-value {
          color: #bef7ff;
          font-weight: $bold;
          font-size: rem-calc(36);
          position: absolute;
          line-height: 0;
          top: 4px;
        }

        .value {
          @include charter-black;
          font-size: rem-calc(24);
          display: block;
        }

        .title {
          display: block;
          font-weight: $bold;
        }

        .value + .title {
          margin-top: 4px;
        }

        .empty-value + .title {
          display: inline-block;
          margin-left: rem-calc(32);
        }

        .description {
          display: block;
          margin-top: rem-calc(12);
          line-height: 1.33;
        }
      }
    }
  }

  @include breakpoint(small) {
    &.expanded {
      height: 712px;
    }
    .pop-details {
      height: 100%;

      .pop-arc-card {
        display: block;
        max-height: rem-calc(252);
        height: auto;
      }

      .pop-aspect-card-container {
        display: block;
        margin-left: 0;
        width: 100%;

        .pop-aspect-card {
          height: auto;
          width: 100%;
          display: block;
          margin: rem-calc(16) 0;
        }
      }
    }
  }
}
</style>
